import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Combobox,
  type ComboboxProps,
  Option,
  ProgressBar,
  makeStyles,
} from '@fluentui/react-components';

export interface ISuggestionItem {
  key: string;
  text: string;
}

export interface AutoCompleteProps {
  /** Async function that fetches suggestions for the given search text */
  onFetch?: (
    searchText: string,
    abortController: AbortController
  ) => Promise<ISuggestionItem[]>;
  /** Debounce time in ms before fetch fires (default: 300) */
  debounceTime?: number;
  /** Fires when user selects a suggestion */
  onSelect?: (suggestion: ISuggestionItem) => void;
  /** Fires when user clears the input */
  onClear?: () => void;
  /** Displayed text in the input */
  value?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Custom id */
  id?: string;
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
}

const useStyles = makeStyles({
  root: {
    width: '100%',
    minWidth: 0,
  },
  loadingBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
  },
});

const HighlightText = ({ text, query }: { text: string; query: string }) => {
  if (!query) return <>{text}</>;
  try {
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => (
          <span
            key={i}
            style={
              part.toLowerCase() === query.toLowerCase()
                ? { fontWeight: 600 }
                : undefined
            }
          >
            {part}
          </span>
        ))}
      </span>
    );
  } catch {
    return <>{text}</>;
  }
};

const AutoComplete = ({
  onFetch,
  debounceTime = 300,
  onSelect,
  onClear,
  value,
  placeholder,
  disabled = false,
  id,
  className,
  style,
}: AutoCompleteProps) => {
  const styles = useStyles();
  const [query, setQuery] = useState(value ?? '');
  const [suggestions, setSuggestions] = useState<ISuggestionItem[]>();
  const [inProgress, setInProgress] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const abortControllerRef = useRef<AbortController>(null);
  const onFetchRef = useRef(onFetch);
  const justSelectedRef = useRef(false);
  const userTypedRef = useRef(false);

  // Keep ref in sync without triggering effects
  onFetchRef.current = onFetch;

  useEffect(() => {
    userTypedRef.current = false;
    setOpen(false);
    setSuggestions(undefined);
    if (value != null) setQuery(value);
  }, [value]);

  // Debounce: only fetch after the user types, not when hydrating a saved value
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    if (!userTypedRef.current || disabled || !query) return;

    debounceRef.current = setTimeout(() => {
      if (!onFetchRef.current) return;

      // Abort previous request
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setInProgress(true);
      onFetchRef
        .current(query, abortController)
        .then((data) => {
          setSuggestions(data);
        })
        .catch(() => {
          setSuggestions([]);
        })
        .finally(() => {
          setInProgress(false);
        });
    }, debounceTime);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, debounceTime, disabled]);

  // Show dropdown when suggestions arrive from a user search
  useEffect(() => {
    if (!userTypedRef.current) return;
    setOpen(suggestions !== undefined && suggestions.length > 0);
  }, [suggestions]);

  const handleInput = useCallback<NonNullable<ComboboxProps['onInput']>>(
    (e) => {
      const newValue = e.currentTarget.value;
      userTypedRef.current = true;
      setQuery(newValue);

      if (!newValue || newValue.trim() === '') {
        setOpen(false);
        setSuggestions(undefined);
        onClear?.();
      }
    },
    [onClear]
  );

  const handleOptionSelect: NonNullable<ComboboxProps['onOptionSelect']> =
    useCallback(
      (_e, data) => {
        if (!data.optionValue || data.optionValue === '__loading__') return;

        const selected = suggestions?.find((s) => s.key === data.optionValue);
        if (selected) {
          justSelectedRef.current = true;
          setQuery(selected.text);
          setOpen(false);
          onSelect?.(selected);
        }
      },
      [suggestions, onSelect]
    );

  const handleOpenChange: NonNullable<ComboboxProps['onOpenChange']> =
    useCallback(
      (_e, data) => {
        if (data.open && !suggestions?.length && !inProgress) return;
        setOpen(data.open);
      },
      [suggestions, inProgress]
    );

  return (
    <div className={className} style={{ position: 'relative', ...style }}>
      <Combobox
        id={id}
        className={styles.root}
        freeform
        expandIcon={null}
        placeholder={placeholder}
        value={query}
        open={open}
        onInput={handleInput}
        onOptionSelect={handleOptionSelect}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        autoComplete="off"
      >
        {inProgress && (
          <Option key="__loading__" value="__loading__" disabled text="">
            <ProgressBar />
          </Option>
        )}
        {suggestions?.map((suggestion) => (
          <Option
            key={suggestion.key}
            value={suggestion.key}
            text={suggestion.text}
          >
            <HighlightText text={suggestion.text} query={query} />
          </Option>
        ))}
        {!inProgress && suggestions && suggestions.length === 0 && query && (
          <Option key="__empty__" value="__empty__" disabled text="">
            No results found
          </Option>
        )}
      </Combobox>
      {inProgress && <ProgressBar className={styles.loadingBar} />}
    </div>
  );
};

export default AutoComplete;
